import Guid from "common/values/guid/guid";
import IndividualProfile, { IndividualProfileJSON } from "marketplace/values/individual-profile/individual-profile";
import Company, { CompanyJSON } from "marketplace/entities/company/company";

export interface IndividualJSON {
  id?: string;
  userId: string;
  profile?: IndividualProfileJSON;
  entityId?: string;
  company?: CompanyJSON;
  isVisible?: boolean;
}

export default class Individual {
  id: Guid;
  userId: Guid;
  profile?: IndividualProfile;
  entityId?: Guid;
  company?: Company;
  isVisible: boolean = false;

  constructor(
    id: Guid,
    userId: Guid,
    profile?: IndividualProfile,
    company?: Company,
    isVisible?: boolean
  ) {

    this.id = id;
    this.userId = userId;
    this.profile = profile;
    this.entityId = company?.entityId;
    this.company = company;
    this.isVisible = isVisible ?? false;
  }

  public get isEmpty(): boolean {
    return false;
  }

  public getFullName(): string {
    if (!this.profile) return 'N/A';
    return this.profile.getFullName();
  }

  public toString(): string {
    return this.getFullName();
  }

  public getInitials(): string {
    if (!this.profile) return 'N/A';
    return `${this.profile.firstName[0]}${this.profile.lastName[0]}`;
  }

  public clone(): Individual {
    return new Individual(this.id, this.userId, this.profile, this.company, this.isVisible);
  }

  public isEqualTo(other: Individual | null | undefined): boolean {
    if (!other) return false;
    return this.userId!.isEqualTo(other.id) || this.userId!.isEqualTo(other.userId)
  }

  public static fromJSON(json: IndividualJSON) : Individual {
    if (!json) {
      throw new Error("No JSON provided");
    }

    const individualId = json.id ? Guid.fromJSON(json.id) : Guid.generate();
    const userId = Guid.fromJSON(json.userId);
    const profile = json.profile ? IndividualProfile.fromJSON(json.profile) : undefined;
    let company: Company | undefined = undefined;
    if (json.company) {
      company = Company.fromJSON(json.company);
    } else if (json.entityId) {
      company = new Company(Guid.generate(), Guid.fromJSON(json.entityId));
    }

    if (!userId) throw new Error("User Id is required");

    return new Individual(
      individualId,
      userId,
      profile, 
      company, 
      json.isVisible
    );
  }
  public fromJSON(json: IndividualJSON): Individual | null {
    if (!json) return null;
    return Individual.fromJSON(json);
  }
  public toJSON(): IndividualJSON {
    return {
      id: this.id.toJSON(),
      userId: this.userId.toJSON(),
      profile: this.profile?.toJSON(),
      entityId: this.entityId?.toJSON(),
      company: this.company?.toJSON(),
      isVisible: this.isVisible
    }
  }

  public static get Prototype(): Individual {
      const individualId = Guid.generate();
      const company = new Company(Guid.generate(), Guid.generate());
      const individualProfile = new IndividualProfile(undefined, "John", "Doe");
      const individual = new Individual(
        individualId,
        Guid.generate(),
        individualProfile,
        company,
        true
      );    
    return individual;
  }
}
